import { Component, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MapGeocoder } from '@angular/google-maps';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import GeoJSON from 'ol/format/GeoJSON.js';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { OSM } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import View from 'ol/View';
import { MessageModule } from 'primeng/message';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { defaults as defaultsControls } from 'ol/control';
import { defaults as defaultsInteractions } from 'ol/interaction';
export interface MapFieldProps extends FormlyFieldProps {
  enableMarkerMove: boolean;
  enableMarker: boolean;
  enableCentroid: boolean;
  enableInteraction: boolean;
  styleClassMap: string;
  padding: number[];
  isDrawerOn: boolean;
  init: any;
  inputLabel: string;
  inputPlaceholder: string;
  inputBtnLabel: string;
}

@Component({
  selector: 'map-field',
  standalone: true,
  imports: [...FORM_IMPORTS, MessageModule, TranslatePipe],
  template: `
    <div class="flex flex-col gap-4">
      <form [formGroup]="inputForm" class="flex flex-col w-full">
        <formly-form
          [model]="inputModel"
          [fields]="fields"
          [form]="inputForm"
          [options]="inputOptions"
        ></formly-form>
      </form>
      @if (formControl.value && formControl.value.formatted_address) {
        <div>{{ formControl.value.formatted_address }}</div>
      }
      <div [id]="field.key" [className]="props.styleClassMap"></div>

      @if (formControl.valid) {
        <div
          class="p-message p-component ng-tns-c3516464933-14 ng-trigger ng-trigger-messageAnimation p-message-warn ng-star-inserted"
          aria-live="polite"
          role="alert"
        >
          <div class="p-message-content ng-tns-c3516464933-14">
            <span
              class="ng-tns-c3516464933-14 p-message-text ng-star-inserted"
              style=""
              >{{ 'map.address.conferma' | translate }}</span
            >
          </div>
        </div>
      }
    </div>
  `,
})
export class MapFieldType extends FieldType<FieldTypeConfig<MapFieldProps>> {
  private readonly _geocoder = inject(MapGeocoder);
  map?: Map;
  view = new View({
    projection: 'EPSG:4326',
    maxZoom: 18,
    center: [0, 0],
    zoom: 1,
  });
  areaSource = new VectorSource({ wrapX: false });
  assetsSource = new VectorSource({ wrapX: false });
  centerSource = new VectorSource({ wrapX: false });
  cerConfigSource = new VectorSource({ wrapX: false });
  inputForm = new FormGroup({});
  inputModel?: any = {};
  inputOptions: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];

  //confirmAddress(confirm: boolean) {}
  constructor() {
    super();
    this.fields = [
      { key: 'point' },
      {
        key: 'addressInput',
        type: 'address',
        wrappers: ['search'],
        props: {
          search: () => {},
          onClick: (field: any) => {
            field.field.props.search(field.field.formControl.value);
          },
        },
        expressions: {
          'props.placeholder': () => this.props.inputPlaceholder,
          'props.buttonLabel': () => this.props.inputBtnLabel,
        },
      },
    ];
  }
  init() {
    this.map = new Map({
      view: this.view,
      controls: defaultsControls({
        rotate: false,
        zoom: false,
        attribution: false,
      }),
      interactions: defaultsInteractions({
        altShiftDragRotate: false,
        pinchRotate: false,
      }),
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        new VectorLayer({
          source: this.centerSource,
          style: new Style({
            image: new Icon({
              anchor: [0.5, 42.556],
              anchorXUnits: 'fraction',
              anchorYUnits: 'pixels',
              src: 'map-icons/marker.svg',
              scale: 0.7,
            }),
          }),
        }),
      ],
      target: this.field.key as string,
    });
    this.map.on('click', (evt) => {
      const { coordinate } = evt;
      const point = {
        type: 'Point',
        coordinates: coordinate,
      };
      this.inputForm.patchValue({ point, addressInput: undefined });
    });
    this.centerSource.clear();

    if (
      this.formControl.value &&
      (this.formControl.value.point || this.formControl.value.geometry)
    ) {
      const geometry = this.formControl.value.point ?? {
        type: 'Point',
        coordinates: [
          this.formControl.value.geometry.location.lng(),
          this.formControl.value.geometry.location.lat(),
        ],
      };

      const centerFeature = new GeoJSON().readFeatures(geometry);
      this.centerSource.addFeatures(centerFeature);
      this.view.animate({
        center: geometry.coordinates,
        duration: 0,
        zoom: 16,
      });
    } else {
      const feature = new GeoJSON().readFeatures({
        type: 'Polygon',
        crs: {
          type: 'name',
          properties: {
            name: 'urn:ogc:def:crs:EPSG::4326',
          },
        },
        coordinates: [
          [
            [6.627265, 35.288961],
            [18.784475, 35.288961],
            [18.784475, 47.092146],
            [6.627265, 47.092146],
            [6.627265, 35.288961],
          ],
        ],
      });
      this.view.fit(feature[0].getGeometry()!.getExtent(), {
        duration: 0,
      });
    }
  }

  ngOnInit() {
    this.field.props!['init'] = this.init.bind(this);
    setTimeout(() => {
      this.inputForm.valueChanges.subscribe((res: any) => {
        if (res.addressInput && typeof res.addressInput === 'object') {
          const location = res.addressInput.geometry.location;
          const geometry = {
            type: 'Point',
            coordinates: [location.lng(), location.lat()],
          };
          this.centerSource.clear();
          const centerFeature = new GeoJSON().readFeatures(geometry);
          this.centerSource.addFeatures(centerFeature);
          this.view.animate({
            center: geometry.coordinates,
            duration: 500,
            zoom: 16,
          });
          this.formControl.setValue(this.inputModel.addressInput);

          setTimeout(() => {
            this.inputForm.reset();
          });
        } else if (res.point) {
          this.centerSource.clear();
          const centerFeature = new GeoJSON().readFeatures(res.point);
          this.centerSource.addFeatures(centerFeature);
          this.view.animate({
            center: res.point.coordinates,
            duration: 500,
            zoom: 16,
          });
          this.formControl.setValue({ point: res.point });
        }
      });
    });
  }
}
